import { isInternalUserId, type AuthMethod } from './events'

const AUTH_METHOD_COOKIE_NAME = 'evirma_auth_method'
const AUTH_METHOD_COOKIE_TTL_SECONDS = 300
const AUTH_METHODS: readonly AuthMethod[] = ['password', 'mailru', 'vk', 'telegram', 'google']

export interface BrowserCookieAccessor {
  get(): string
  set(value: string): void
}

export interface ServerCookieWriter {
  set(
    name: string,
    value: string,
    options: {
      httpOnly: boolean
      maxAge: number
      path: string
      sameSite: 'lax'
      secure: boolean
    }
  ): void
}

interface CookieEnvironment {
  isHttps: boolean
  isProduction: boolean
}

export interface BrowserAuthMethodMarkerConsumption {
  authMethod?: AuthMethod
  isKnown: boolean
}

const defaultBrowserCookieAccessor: BrowserCookieAccessor = {
  get: () => (typeof document === 'undefined' ? '' : document.cookie),
  set: (value) => {
    if (typeof document !== 'undefined') {
      document.cookie = value
    }
  },
}

const getBrowserCookieEnvironment = (): CookieEnvironment => ({
  isHttps: typeof location !== 'undefined' && location.protocol === 'https:',
  isProduction: process.env.NODE_ENV === 'production',
})

const serializeBrowserCookie = (value: string, maxAge: number, environment: CookieEnvironment): string =>
  [
    `${AUTH_METHOD_COOKIE_NAME}=${value}`,
    `Max-Age=${maxAge}`,
    'Path=/',
    'SameSite=Lax',
    ...(environment.isHttps || environment.isProduction ? ['Secure'] : []),
  ].join('; ')

const readCookieValue = (cookies: string): string | undefined => {
  const cookie = cookies
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${AUTH_METHOD_COOKIE_NAME}=`))

  return cookie?.slice(AUTH_METHOD_COOKIE_NAME.length + 1) || undefined
}

const isAuthMethod = (value: string | undefined): value is AuthMethod =>
  value !== undefined && AUTH_METHODS.includes(value as AuthMethod)

export const getAuthMethodMarkerAction = ({
  isImpersonating,
  userId,
}: {
  isImpersonating: boolean
  userId?: string
}): 'consume' | 'discard' | 'preserve' => {
  if (isImpersonating) {
    return 'discard'
  }
  if (!userId) {
    return 'preserve'
  }

  return isInternalUserId(userId) ? 'consume' : 'discard'
}

export const writeBrowserAuthMethodMarker = (
  authMethod: AuthMethod,
  cookies: BrowserCookieAccessor = defaultBrowserCookieAccessor,
  environment: CookieEnvironment = getBrowserCookieEnvironment()
): boolean => {
  try {
    cookies.set(serializeBrowserCookie(authMethod, AUTH_METHOD_COOKIE_TTL_SECONDS, environment))
    return true
  } catch {
    return false
  }
}

export const readBrowserAuthMethodMarker = (
  cookies: BrowserCookieAccessor = defaultBrowserCookieAccessor
): AuthMethod | undefined => {
  try {
    const value = readCookieValue(cookies.get())

    return isAuthMethod(value) ? value : undefined
  } catch {
    return undefined
  }
}

export const clearBrowserAuthMethodMarker = (
  cookies: BrowserCookieAccessor = defaultBrowserCookieAccessor,
  environment: CookieEnvironment = getBrowserCookieEnvironment()
): boolean => {
  try {
    cookies.set(serializeBrowserCookie('', 0, environment))
    return true
  } catch {
    return false
  }
}

export const consumeBrowserAuthMethodMarker = (
  cookies: BrowserCookieAccessor = defaultBrowserCookieAccessor,
  environment: CookieEnvironment = getBrowserCookieEnvironment()
): BrowserAuthMethodMarkerConsumption => {
  let authMethod: AuthMethod | undefined
  try {
    const value = readCookieValue(cookies.get())
    authMethod = isAuthMethod(value) ? value : undefined
  } catch {
    return { isKnown: false }
  }

  if (!clearBrowserAuthMethodMarker(cookies, environment)) {
    return { isKnown: false }
  }

  try {
    if (readCookieValue(cookies.get()) !== undefined) {
      return { isKnown: false }
    }
  } catch {
    return { isKnown: false }
  }

  return { authMethod, isKnown: true }
}

export const writeServerAuthMethodMarker = (
  cookies: ServerCookieWriter,
  authMethod: AuthMethod,
  environment: CookieEnvironment
): boolean => {
  try {
    cookies.set(AUTH_METHOD_COOKIE_NAME, authMethod, {
      httpOnly: false,
      maxAge: AUTH_METHOD_COOKIE_TTL_SECONDS,
      path: '/',
      sameSite: 'lax',
      secure: environment.isHttps || environment.isProduction,
    })
    return true
  } catch {
    return false
  }
}
